/**
 * ContainerTimelineList — reusable presentation component for the unified
 * container timeline. Used by both the right-click `ContainerTimelineDialog`
 * and the in-load "Event Logs" tab in `EditLoadSheet` / `EditExportSheet`.
 *
 * Pure presentation: the parent decides where the data comes from (typically
 * `GET /api/shipments/{id}/timeline`) and passes events + loading + error.
 *
 * Props
 *   events   — array of timeline event objects { type, timestamp, title, description, user_name, icon }
 *   loading  — boolean
 *   error    — string | null
 *   className — optional wrapper class override
 *   emptyMessage — optional custom empty-state copy
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Loader2, Package, Activity, Edit3, Truck, MessageSquare,
} from 'lucide-react';

const ICONS = {
  package: Package,
  activity: Activity,
  edit: Edit3,
  truck: Truck,
  'message-square': MessageSquare,
};

const TYPE_COLORS = {
  created: 'text-emerald-300 bg-emerald-500/10 ring-emerald-500/30',
  status_change: 'text-sky-300 bg-sky-500/10 ring-sky-500/30',
  dispatch: 'text-amber-300 bg-amber-500/10 ring-amber-500/30',
  comment: 'text-blue-300 bg-blue-500/10 ring-blue-500/30',
};

const colorForType = (type) => {
  if (type?.startsWith('activity')) return 'text-violet-300 bg-violet-500/10 ring-violet-500/30';
  return TYPE_COLORS[type] || 'text-slate-300 bg-slate-500/10 ring-slate-500/30';
};

const formatStamp = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

export const ContainerTimelineList = ({
  events = [],
  loading = false,
  error = null,
  className = '',
  emptyMessage = 'No events yet for this container.',
}) => {
  // Detect a freshly-arrived event so we can briefly pulse it amber. We
  // remember the previously-newest timestamp; whenever it changes (and we're
  // not on the very first render), the new top row gets a one-shot animation.
  const lastSeenTs = useRef(null);
  const [freshTs, setFreshTs] = useState(null);
  useEffect(() => {
    if (!events.length) return;
    const topTs = events[0]?.timestamp || null;
    if (lastSeenTs.current && topTs && topTs !== lastSeenTs.current) {
      Promise.resolve().then(() => setFreshTs(topTs));
      // Reset after the animation finishes so the row settles to baseline.
      const t = setTimeout(() => setFreshTs(null), 1400);
      lastSeenTs.current = topTs;
      return () => clearTimeout(t);
    }
    lastSeenTs.current = topTs;
  }, [events]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center py-12 text-slate-400 ${className}`}>
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading timeline…
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`py-8 text-center text-sm text-rose-400 ${className}`}
        data-testid="container-timeline-error"
      >
        {error}
      </div>
    );
  }

  if (!events.length) {
    return (
      <div
        className={`py-8 text-center text-sm text-slate-500 ${className}`}
        data-testid="container-timeline-empty"
      >
        {emptyMessage}
      </div>
    );
  }

  return (
    <>
      {/* One-shot amber pulse for the freshest event when the list refreshes */}
      <style>{`
        @keyframes timeline-fresh-pulse {
          0%   { background-color: rgba(245, 158, 11, 0.22); box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.18); }
          70%  { background-color: rgba(245, 158, 11, 0.10); box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.08); }
          100% { background-color: transparent;             box-shadow: 0 0 0 0 transparent; }
        }
        .timeline-event--fresh {
          animation: timeline-fresh-pulse 1.3s ease-out 1;
          border-radius: 6px;
          margin: -4px -8px;
          padding: 4px 8px;
        }
      `}</style>
      <ol
        className={`relative border-l border-slate-700 ml-3 pl-6 py-2 space-y-4 ${className}`}
        data-testid="container-timeline-list"
      >
        {events.map((e, idx) => {
          const IconCmp = ICONS[e.icon] || Activity;
          const colorCls = colorForType(e.type);
          const isFresh = freshTs && e.timestamp === freshTs;
          return (
            <li
              key={`${e.type}-${e.timestamp}-${idx}`}
              className={`relative ${isFresh ? 'timeline-event--fresh' : ''}`}
              data-testid={`timeline-event-${idx}`}
              data-fresh={isFresh ? 'true' : undefined}
            >
              <span
                className={`absolute -left-9 top-0.5 flex h-6 w-6 items-center justify-center rounded-full ring-2 ${colorCls}`}
              >
                <IconCmp className="h-3 w-3" />
              </span>
              <div className="flex items-baseline justify-between gap-3">
                <h4 className="text-sm font-semibold text-slate-100">{e.title}</h4>
                <time className="shrink-0 text-[11px] text-slate-500">
                  {formatStamp(e.timestamp)}
                </time>
              </div>
              {e.description && (
                <p className="mt-1 text-xs text-slate-300 whitespace-pre-wrap break-words">
                  {e.description}
                </p>
              )}
              {e.user_name && (
                <p className="mt-1 text-[11px] text-slate-500">by {e.user_name}</p>
              )}
            </li>
          );
        })}
      </ol>
    </>
  );
};

export default ContainerTimelineList;
